<template>
  <div class="bg-white dark:bg-neutral-800 pb-4 rounded-md min-w-0 shadow">
    <TheNuxtLink :to="href">
      <AsyncImage
        :src="banner"
        class="object-cover rounded-t-md w-full aspect-[7/2]"
      />
      <div class="-mt-12 px-4 w-full flex flex-col items-center justify-center">
        <UserAvatar
          :user="user"
          :size="80"
          :class="{
            'border-2 border-black dark:border-white !rounded-full': !(
              user as UserEntity
            ).relationships?.find(isFrame),
          }"
        />
        <div class="flex w-full items-center justify-center mt-1 gap-1">
          <BadgeUsername
            class="font-medium xl:text-lg 2xl:text-xl"
            :badges
            :official
            :verified
          >
            {{ displayName }}
          </BadgeUsername>
        </div>
        <div
          class="truncate text-xs xl:text-sm 2xl:text-base text-neutral-600 dark:text-neutral-400 mb-1"
        >
          @{{ username }}
        </div>
        <div
          class="text-xs lg:text-sm opacity-70 flex items-center mb-1"
          :class="{ invisible: !followsYou }"
        >
          <IconUser :size="10" class="mr-0.5" /> Follows You
        </div>
        <NamiButton
          :class="{ invisible: authStore?.user?.profile.sub === user?.id }"
          :small="sm"
          block
          :buttonType="
            following || authStore?.user?.profile.sub === user?.id
              ? 'secondary'
              : 'primary'
          "
          @click.stop.prevent="following ? unfollow() : follow()"
          :disabled="
            isPendingAction ||
            authStore?.user?.profile.sub === user?.id ||
            disableFollow
          "
        >
          {{ following ? "Unfollow" : "Follow" }}
        </NamiButton>
      </div>
    </TheNuxtLink>
  </div>
</template>

<script setup lang="ts">
import {
  isFrame,
  type PopulateRelationship,
  User,
  type UserEntity,
  type UserRelation,
} from "~/src/api";
import { IconUser } from "@tabler/icons-vue";
import { parseUserForDisplay } from "~/utils/display/user";

const props = defineProps<{
  user: UserEntity | PopulateRelationship<UserRelation>;
  following: boolean;
  followsYou?: boolean;
  disableFollow?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:following", v: boolean): void;
}>();

const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();
const appStore = nuxtApp.$app();
const { sm } = useBreakpoints();
const {
  username,
  displayName,
  verified,
  official,
  avatar,
  banner,
  href,
  badges,
} = parseUserForDisplay(props.user, "small");

const isPendingAction = ref(false);

async function follow() {
  if (!authStore?.user) {
    return appStore?.openLoginRequiredModal();
  }

  if (!props.user) {
    return;
  }

  isPendingAction.value = true;

  await executeWithNotificationOnError(async () => {
    const token = await authStore?.getToken();
    if (!token) throw new Error("Not logged in.");

    await User.follow(props.user!.id, token);
  }).catch(() => {});
  emit("update:following", true);
  isPendingAction.value = false;
}

async function unfollow() {
  if (!props.user) {
    return;
  }

  isPendingAction.value = true;

  await executeWithNotificationOnError(async () => {
    const token = await authStore?.getToken();
    if (!token) throw new Error("Not logged in.");

    await User.unfollow(props.user!.id, token);
  }).catch(() => {});

  emit("update:following", false);
  isPendingAction.value = false;
}
</script>
